import * as RadixSelect from '@radix-ui/react-select';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cx } from 'class-variance-authority';
import { forwardRef } from 'react';
import BaseSelectTrigger from '../../../internal/base-select-trigger';
import type { Size } from '../../select.types';

type SelectTriggerProps = {
  value: string;
  placeholder: string;
  disabled: boolean;
  hasError: boolean;
  size: Size;
  className?: string;
  ariaLabel?: string;
  customTrigger?: React.ReactNode;
  findOptionLabel: (value: string) => string | React.ReactNode;
  id?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
};

const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  (
    {
      value,
      placeholder,
      disabled,
      hasError,
      size,
      className,
      ariaLabel,
      customTrigger,
      findOptionLabel,
      id,
      onBlur,
      onFocus,
      autoFocus,
      ...rest
    }: SelectTriggerProps,
    ref,
  ) => {
    if (customTrigger) {
      return (
        <RadixSelect.Trigger
          id={id}
          asChild
          onBlur={onBlur}
          onFocus={onFocus}
          ref={ref}
          disabled={disabled}
          tabIndex={0}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
        >
          <div
            {...rest}
            translate="no"
            className={cx('flex flex-row focus-visible:outline-none cursor-pointer disabled:cursor-default', className)}
          >
            {customTrigger}
            <VisuallyHidden>
              <RadixSelect.Value>{value}</RadixSelect.Value>
            </VisuallyHidden>
          </div>
        </RadixSelect.Trigger>
      );
    }

    return (
      <div {...rest} translate="no" className={className}>
        <RadixSelect.Trigger
          aria-label={ariaLabel}
          asChild
          className={className}
          autoFocus={autoFocus}
          disabled={disabled}
          onFocus={onFocus}
        >
          <BaseSelectTrigger
            size={size}
            data-has-error={hasError}
            isEmpty={!value}
            tabIndex={0}
            id={id}
            ref={ref}
            onBlur={onBlur}
            disabled={disabled}
            className="flex focus-visible:outline-none cursor-pointer disabled:cursor-default"
            autoFocus={autoFocus}
          >
            <RadixSelect.Value placeholder={placeholder} className="truncate text-body-2">
              {value ? findOptionLabel(value) : null}
            </RadixSelect.Value>
          </BaseSelectTrigger>
        </RadixSelect.Trigger>
      </div>
    );
  },
);

export default SelectTrigger;
